import { DomUtilsService } from "../../../core/services/dom-utils.service";
import { HtmlService } from "../../../core/services/html.service";
import { NS, STYLES, REFERENCES, ICONS, SCROLL_TO_BOTTOM_THRESHOLD, CLICK, SCROLL, DOT } from "../../constants";
import type { IChatOptions } from "../../models";
import type { IKendoJQuery } from "../../../core";

declare const kendo: any;
declare const $: any;

export interface IScrollManagerContext {
    element: IKendoJQuery;
    options: IChatOptions;
    list: IKendoJQuery;
    domUtilsService: DomUtilsService;
    htmlService: HtmlService;
}

export class ScrollManager {
    private context: IScrollManagerContext;
    private scrollToBottomButton: IKendoJQuery | null = null;
    private _scrollHandler: (() => void) | null = null;
    private _clickHandler: ((e: any) => void) | null = null;

    constructor(context: IScrollManagerContext) {
        this.context = context;
        this._initScrollToBottomButton();
        this._attachEvents();
    }

    private _initScrollToBottomButton(): void {
        const options = this.context.options;

        if (options.scrollToBottomButton === false) {
            this.scrollToBottomButton = null;
            return;
        }

        const buttonHtml = kendo.html.renderButton(
            `<button class="${STYLES.scrollFab} ${STYLES.hidden}" ${REFERENCES.scrollToBottomButton} aria-label="Scroll to bottom"></button>`,
            {
                icon: ICONS.arrowDown,
                rounded: "full"
            }
        );

        this.scrollToBottomButton = $(buttonHtml).appendTo(this.context.element);
        const cssStyles = {
            position: "sticky",
            bottom: "16px",
            transform: "translateX(-50%)",
            zIndex: 1
        }

        const isContextElementRtl = this.context.element.css("direction") === "rtl";
        cssStyles[isContextElementRtl ? "right" : "left"] = "50%";

        this.scrollToBottomButton.css(cssStyles);
    }

    private _attachEvents(): void {
        this._scrollHandler = this._onScroll.bind(this);
        this._clickHandler = this._scrollToBottomButtonClick.bind(this);

        this.context.element
            .on(SCROLL + NS + ".scrollManager", this._scrollHandler)
            .on(CLICK + NS + ".scrollManager", `[${REFERENCES.scrollToBottomButton}]`, this._clickHandler);
    }

    private _updateScrollToBottomButtonVisibility(): void {
        if (!this.scrollToBottomButton) {
            return;
        }

        const scrollTop = this.context.element.scrollTop() || 0;
        const scrollHeight = this.context.element.prop("scrollHeight") || 0;
        const clientHeight = this.context.element.prop("clientHeight") || 0;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceFromBottom > SCROLL_TO_BOTTOM_THRESHOLD) {
            this.scrollToBottomButton.removeClass(STYLES.hidden);
        } else {
            this.scrollToBottomButton.addClass(STYLES.hidden);
        }
    }

    private _onScroll(): void {
        this._updateScrollToBottomButtonVisibility();
    }

    private _scrollToBottomButtonClick(e: any): void {
        e.preventDefault();
        this.scrollToBottom();
    }

    scrollToBottom(): void {
        this.context.element.scrollTop(this.context.element.prop("scrollHeight"));
    }

    scrollToMessage(uid: string): boolean {
        if (!uid) {
            return false;
        }

        const messageElement = this.context.list.find(DOT + STYLES.message + `[data-uid="${this.context.htmlService.encode(uid)}"]`);

        if (!messageElement.length) {
            return false;
        }

        const pinnedElement = this.context.element.find(DOT + STYLES.messagePinned);
        const pinnedHeight = pinnedElement.length ? pinnedElement.outerHeight() || 0 : 0;

        return this.context.domUtilsService.scrollToElement(this.context.element, messageElement, { position: "top", offset: -pinnedHeight });
    }

    destroy(): void {
        if (this.scrollToBottomButton) {
            this.scrollToBottomButton.remove();
            this.scrollToBottomButton = null;
        }

        this.context.element.off(NS + ".scrollManager");
    }
}
